import SecondaryNavigation from "@/components/navigation/SecondaryNavigation";
import { secondaryNavigationData } from "@/components/navigation/navigationData";
import { filterNavItemsByRole } from "@/components/utils/adminUtils";

// Helper to set current section and filter by user role
const getNavWithCurrent = (currentName: string, userRole?: string) => {
  // First filter by role, then mark current section
  const filteredNav = filterNavItemsByRole(secondaryNavigationData, userRole);
  return filteredNav.map((item) => ({
    ...item,
    current: item.name === currentName,
  }));
};

import React, { useState } from "react";
import { Switch } from "@headlessui/react";
import { classNames } from "@/components/utils/functions";

const notificationDefaults = {
  email: true,
  sms: false,
  push: true,
  frequency: "immediate",
};

const frequencyOptions = [
  { value: "immediate", label: "Immediate" },
  { value: "daily", label: "Daily Digest" },
  { value: "weekly", label: "Weekly Digest" },
];

export default function NotificationsSettings() {
  const [settings, setSettings] = useState(notificationDefaults);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);

  const handleToggle = (key: keyof typeof notificationDefaults) => {
    setSettings((prev) => ({ ...prev, [key]: !prev[key] }));
    setSaved(false);
  };

  const handleFrequencyChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    setSettings((prev) => ({ ...prev, frequency: e.target.value }));
    setSaved(false);
  };

  const handleSave = (e: React.FormEvent) => {
    e.preventDefault();
    setSaving(true);
    // Simulate API call
    setTimeout(() => {
      setSaving(false);
      setSaved(true);
    }, 800);
  };

  return (
    <main className="bg-gray-50 dark:bg-gray-900 text-gray-900 dark:text-gray-100 min-h-screen">
      <div className="mx-auto max-w-7xl lg:flex lg:gap-x-16 lg:px-8">
        <h1 className="sr-only">Notifications Settings</h1>
        <SecondaryNavigation items={getNavWithCurrent("Notifications")} />
        <main className="px-4 py-16 sm:px-6 lg:flex-auto lg:px-0 lg:py-10">
          <div className="mx-auto w-full lg:mx-0 lg:max-w-none">
            <h2 className="text-xl font-semibold">Notifications Settings</h2>
            <p className="mt-2 text-gray-500 dark:text-gray-400">
              Configure how you receive notifications from the app.
            </p>
            <section className="mt-10 bg-white dark:bg-gray-800 rounded-lg shadow p-6 pt-3 border border-gray-200 dark:border-gray-700">
              <form onSubmit={handleSave}>
                <dl className="space-y-6 divide-y divide-gray-200 dark:divide-gray-700 border-gray-200 dark:border-gray-700 text-sm leading-6">
                  {/* Email Notification Switch */}
                  <Switch.Group as="div" className="flex pt-6">
                    <Switch.Label
                      as="dt"
                      className="flex-none pr-6 font-medium text-gray-900 dark:text-gray-100 sm:w-64"
                      passive
                    >
                      Email notifications
                    </Switch.Label>
                    <dd className="flex flex-auto items-center justify-end">
                      <Switch
                        checked={settings.email}
                        onChange={() => handleToggle("email")}
                        className={classNames(
                          settings.email
                            ? "bg-indigo-600"
                            : "bg-gray-200 dark:bg-gray-700",
                          "flex w-8 cursor-pointer rounded-full p-px ring-1 ring-inset ring-gray-900/5 dark:ring-gray-700 transition-colors duration-200 ease-in-out focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-indigo-600"
                        )}
                      >
                        <span
                          aria-hidden="true"
                          className={classNames(
                            settings.email
                              ? "translate-x-3.5"
                              : "translate-x-0",
                            "h-4 w-4 transform rounded-full bg-white dark:bg-gray-200 shadow-sm ring-1 ring-gray-900/5 dark:ring-gray-700 transition duration-200 ease-in-out"
                          )}
                        />
                      </Switch>
                    </dd>
                  </Switch.Group>
                  {/* SMS Notification Switch */}
                  <Switch.Group as="div" className="flex pt-6">
                    <Switch.Label
                      as="dt"
                      className="flex-none pr-6 font-medium text-gray-900 dark:text-gray-100 sm:w-64"
                      passive
                    >
                      SMS notifications
                    </Switch.Label>
                    <dd className="flex flex-auto items-center justify-end">
                      <Switch
                        checked={settings.sms}
                        onChange={() => handleToggle("sms")}
                        className={classNames(
                          settings.sms
                            ? "bg-indigo-600"
                            : "bg-gray-200 dark:bg-gray-700",
                          "flex w-8 cursor-pointer rounded-full p-px ring-1 ring-inset ring-gray-900/5 dark:ring-gray-700 transition-colors duration-200 ease-in-out focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-indigo-600"
                        )}
                      >
                        <span
                          aria-hidden="true"
                          className={classNames(
                            settings.sms ? "translate-x-3.5" : "translate-x-0",
                            "h-4 w-4 transform rounded-full bg-white dark:bg-gray-200 shadow-sm ring-1 ring-gray-900/5 dark:ring-gray-700 transition duration-200 ease-in-out"
                          )}
                        />
                      </Switch>
                    </dd>
                  </Switch.Group>
                  {/* Push Notification Switch */}
                  <Switch.Group as="div" className="flex pt-6">
                    <Switch.Label
                      as="dt"
                      className="flex-none pr-6 font-medium text-gray-900 dark:text-gray-100 sm:w-64"
                      passive
                    >
                      Push notifications
                    </Switch.Label>
                    <dd className="flex flex-auto items-center justify-end">
                      <Switch
                        checked={settings.push}
                        onChange={() => handleToggle("push")}
                        className={classNames(
                          settings.push
                            ? "bg-indigo-600"
                            : "bg-gray-200 dark:bg-gray-700",
                          "flex w-8 cursor-pointer rounded-full p-px ring-1 ring-inset ring-gray-900/5 dark:ring-gray-700 transition-colors duration-200 ease-in-out focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-indigo-600"
                        )}
                      >
                        <span
                          aria-hidden="true"
                          className={classNames(
                            settings.push ? "translate-x-3.5" : "translate-x-0",
                            "h-4 w-4 transform rounded-full bg-white dark:bg-gray-200 shadow-sm ring-1 ring-gray-900/5 dark:ring-gray-700 transition duration-200 ease-in-out"
                          )}
                        />
                      </Switch>
                    </dd>
                  </Switch.Group>
                  {/* Frequency Dropdown */}
                  <div className="flex pt-6">
                    <label
                      htmlFor="frequency"
                      className="flex-none pr-6 font-medium text-gray-900 dark:text-gray-100 sm:w-64"
                    >
                      Notification frequency
                    </label>
                    <dd className="flex flex-auto items-center justify-end">
                      <select
                        id="frequency"
                        name="frequency"
                        className="block w-40 rounded-md border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-600 py-2 pl-3 pr-10 text-sm text-gray-900 dark:text-gray-100 focus:border-indigo-600 focus:ring-indigo-600"
                        value={settings.frequency}
                        onChange={handleFrequencyChange}
                      >
                        {frequencyOptions.map((opt) => (
                          <option key={opt.value} value={opt.value}>
                            {opt.label}
                          </option>
                        ))}
                      </select>
                    </dd>
                  </div>
                </dl>
                <div className="pt-6">
                  <button
                    type="submit"
                    className="inline-flex items-center rounded-md bg-indigo-600 px-3 py-2 text-sm font-semibold text-white shadow-sm hover:bg-indigo-500 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-indigo-600 disabled:bg-indigo-400"
                    disabled={saving}
                  >
                    {saving ? "Saving..." : "Save changes"}
                  </button>
                  {saved && (
                    <span className="ml-4 text-indigo-600 dark:text-indigo-400 text-sm">
                      Settings saved!
                    </span>
                  )}
                </div>
              </form>
            </section>
          </div>
        </main>
      </div>
    </main>
  );
}
